import { UserRole } from '@lmc/ts-common'
import { Button, Icon } from '@lmc/ui-kit'
import { useServiceUserNominative } from 'components/generic/teamCapabilities/useServiceUserNominative'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import { find } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import Selectors from 'selectors'
import styled from 'styled-components'
import { isGroupUser } from 'utils'
import Operations from '../../../../operations'
import { history } from '../../../../store'
import { withRouter } from '../../../../withRouter'
import {
  deriveAssignedResidentNames,
  deriveKeyworkerResidentNames
} from '../../utils/assignment'
import AssignmentCard from './AssignmentCard'
import ClinicalSystemsAccessViewCard from './ClinicalSystemsAccessViewCard'
import EmergencyContactViewCard from './EmergencyContactViewCard'
import EmploymentViewCard from './EmploymentViewCard'
import PersonalDetailsViewCard from './PersonalDetailsViewCard'
import ProfileHeaderCard from './ProfileHeaderCard'

const Container = styled.div`
  width: 100%;
  max-width: 1434px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const PageTitleBar = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 88px;
`
const PageTitleText = styled.h1`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  letter-spacing: 0.24px;
  color: ${({ theme }) => theme.color.neutral.color3};
`
const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const TwoColumnGrid = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    flex-direction: column;
  }
`

const Column = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

type Props = {
  profile?: Record<string, any>
  residents?: any[]
  home?: Record<string, any>
  homesList?: Record<string, any>[]
  keyworkersEnabled?: boolean
  isRosteringBetaEnabled?: boolean
  teamMemberEnabled?: boolean
  currentUserRole?: string
  hasAccess: (roles: string[], abilities: any[]) => boolean
  resendInvite: (id: string, cb: () => void) => void
  fetchCarers: () => void
  router: { params: { id: string } }
}

const TeamMemberProfileScreenV2 = (props: Props) => {
  const {
    profile,
    residents = [],
    home = {},
    currentUserRole,
    hasAccess,
    router,
    fetchCarers
  } = props
  const { t } = useTranslation(NAMESPACE.TEAM)
  const [sendingInvite, setSendingInvite] = useState(false)
  const nominative = useServiceUserNominative()
  const id = router.params.id

  useEffect(() => {
    if (!profile) {
      fetchCarers()
    }
  }, [profile, fetchCarers])

  if (!profile) {
    return null
  }

  // CM-3864: IT can open the edit screen too (the in-form gating restricts them
  // to email + status). The old CASL check was `update User.name`, which IT
  // can't do, so they were locked out entirely — add IT to the role allowlist.
  const canEdit = hasAccess(
    [
      UserRole.careHomeAdmin,
      UserRole.groupAdmin,
      UserRole.groupManager,
      UserRole.management,
      UserRole.it
    ],
    [{ i: 'update', this: 'User', field: 'name' }]
  )
  const canResendInvite = hasAccess(
    [UserRole.careHomeAdmin, UserRole.groupAdmin, UserRole.groupManager],
    [{ i: 'create', this: 'UserInvitation' }]
  )

  const isProfileGroupUser = isGroupUser(profile.role)
  const isCurrentGroupUser = isGroupUser(currentUserRole)
  const showAssignment = !isProfileGroupUser && !isCurrentGroupUser

  const assignedNames = deriveAssignedResidentNames(residents, id)
  const keyworkerNames = deriveKeyworkerResidentNames(residents, id)
  const gpConnectEnabled = !!home.odsCode && !!home.asidCode
  const cernerEnabled = !!home.features?.cerner?.enabled

  const onResendInvite = () => {
    setSendingInvite(true)
    props.resendInvite(profile.id, () => setSendingInvite(false))
  }
  const onEdit = () => history.push(`/organisation/team/${id}/profile/edit`)

  return (
    <Container data-testid="team-member-profile-screen-v2">
      <PageTitleBar data-testid="profile-view-title-bar">
        <PageTitleText>{`${profile.name?.first ?? ''} ${profile.name?.last ?? ''}’s Profile`}</PageTitleText>
        <ButtonGroup>
          <Button
            variant="default"
            disabled={sendingInvite}
            disabledByPermission={!canResendInvite}
            onClick={onResendInvite}
            data-testid="profile-view-resend-invite-button"
          >
            {t(reportsConstants.PROFILE_VIEW_RESEND_INVITE)}
          </Button>
          <Button
            variant="primary"
            disabledByPermission={!canEdit}
            icon={<Icon name="EditOutlined" />}
            onClick={onEdit}
            data-testid="profile-view-edit-button"
          >
            {t(reportsConstants.PROFILE_VIEW_EDIT)}
          </Button>
        </ButtonGroup>
      </PageTitleBar>
      <ProfileHeaderCard
        t={t}
        profile={profile}
        isCurrentGroupUser={isCurrentGroupUser}
        homesList={props.homesList}
      />
      {showAssignment && (
        <AssignmentCard
          t={t}
          assignedNames={assignedNames}
          keyworkerNames={keyworkerNames}
          serviceUser={nominative}
        />
      )}
      <TwoColumnGrid>
        <Column>
          <PersonalDetailsViewCard t={t} profile={profile} />
          <ClinicalSystemsAccessViewCard
            t={t}
            profile={profile}
            gpConnectEnabled={gpConnectEnabled}
            cernerEnabled={cernerEnabled}
            isProfileGroupUser={isProfileGroupUser}
          />
        </Column>
        <Column>
          <EmploymentViewCard t={t} profile={profile} />
          <EmergencyContactViewCard t={t} profile={profile} />
        </Column>
      </TwoColumnGrid>
    </Container>
  )
}

const mapStateToProps = (state: any, ownProps: any) => ({
  profile: find(state.data.carers, { id: ownProps.router.params.id }),
  residents: state.data.residents,
  home: state.global.home,
  homesList: state.data.homes,
  currentUserRole: state.global.user.role,
  hasAccess: (roles: string[], abilities: any[]) =>
    (
      Selectors.checkRoleAccess as (
        s: unknown,
        p: { roles: string[]; abilities: unknown[] }
      ) => boolean
    )(state, { roles, abilities })
})

const mapDispatchToProps = (dispatch: any) => ({
  resendInvite: (userId: string, cb: () => void) =>
    dispatch(Operations.resendInvite(userId, cb)),
  fetchCarers: () => dispatch(Operations.loadList('carers', null, false))
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TeamMemberProfileScreenV2)
)
